// UI-free so non-UI consumers can use these without pulling in @primer/react.

export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high' | 'xhigh' | 'max'

export interface ReasoningEffortOption {
  value: ReasoningEffort
  label: string
  description: string
}

// The reasoning effort the backend applies when no override is sent.
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'medium'

export const REASONING_EFFORT_OPTIONS: ReasoningEffortOption[] = [
  {value: 'none', label: 'None', description: 'No reasoning for the simplest tasks'},
  {value: 'low', label: 'Low', description: 'Faster responses for simpler tasks'},
  {value: 'medium', label: 'Medium', description: 'Balanced reasoning for most tasks'},
  {value: 'high', label: 'High', description: 'More reasoning for complex tasks'},
  {value: 'xhigh', label: 'X-High', description: 'Extended reasoning for harder tasks'},
  {value: 'max', label: 'Max', description: 'Maximum reasoning for the hardest tasks'},
]

export const REASONING_EFFORT_VALUES: ReasoningEffort[] = REASONING_EFFORT_OPTIONS.map(option => option.value)

export function isReasoningEffort(value: unknown): value is ReasoningEffort {
  return typeof value === 'string' && (REASONING_EFFORT_VALUES as string[]).includes(value)
}

export function getReasoningEffortLabel(value?: string): string {
  if (!value) return 'Default'
  return REASONING_EFFORT_OPTIONS.find(option => option.value === value)?.label ?? value
}

// The default level carries a "(Default)" suffix in the menu.
export function getReasoningEffortMenuLabel(value: ReasoningEffort): string {
  const label = REASONING_EFFORT_OPTIONS.find(option => option.value === value)?.label ?? value
  return value === DEFAULT_REASONING_EFFORT ? `${label} (Default)` : label
}

// Caller-side normalization for a held reasoning effort. Returns the value the caller
// should keep for the given supported set: preserved when support is unknown (still
// loading) or the effort is supported, and cleared (undefined) when the model advertises
// a set that excludes it. Picker UIs only clamp the value they display; callers should keep
// their own selectedReasoningEffort in sync with this so they never submit an effort the model
// no longer supports.
export function normalizeReasoningEffort(
  effort: ReasoningEffort | undefined,
  supportedEfforts: readonly string[] | undefined,
): ReasoningEffort | undefined {
  if (!effort || !supportedEfforts) return effort
  return supportedEfforts.includes(effort) ? effort : undefined
}
